import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { createColumnInlineInputNumber, createColumnInlineSelect, createColumnInputNumber } from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { createButtonCreate } from "../../../../../utils/button";
import { conditionFormChecker, onClickDeleteAllClient } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { createManageDelete, createManageExportCsv, createPage } from "../../../../../utils/page";

const Number = defineAsyncComponent(() => import("../../../../../components/table/custom-render/Number"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/hazPackage";

export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });

export const callStroageLocation = ({ searchQuery, refHazLicenseId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
        { name: "isLocation", type: "Boolean", operator: "=", value: true }
    ];
    if (refHazLicenseId) {
        where.push({ name: "hazLicenseStorageLocations.refHazLicense.id", type: "UUID", operator: "=", value: refHazLicenseId });
    }
    if (searchQuery) {
        where.push({ name: "name", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/storageLocation/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "name"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "name", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callStroageLocationSub = ({ searchQuery, refStorageLocationId, notIds }) => {
    const where = [];
    if (refStorageLocationId) {
        where.push({ name: "refStorageLocation.id", type: "UUID", operator: "=", value: refStorageLocationId });
    }
    if (notIds?.length > 0) {
        where.push({ name: "id", type: "UUID", operator: "!in", value: notIds });
    }
    if (searchQuery) {
        where.push({ name: "buildingName", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/storageLocationSub/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "buildingName", "useKg", "useKgReserve", "useKgUse"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "buildingName", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callGetStorageLocationSubReserveAndUse = ({ stroageLocationSubId }) => {
    return store.dispatch("resource/v1/transaction/stockCard/getStorageLocationSubReserveAndUse", { stroageLocationSubId });
};

export const createColumns = ({ props, data }) => {
    const { viewOnly, itemsKey } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    const itemOlds = itemsKey ? lodash.get(initialModel, itemsKey) : modelRef;
    const columns = [];
    columns.push(
        createColumnInlineSelect({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refStorageLocation"),
            dataIndex: "refStorageLocation",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly,
                        list: ({ searchQuery }) =>
                            callStroageLocation({ searchQuery, refHazLicenseId: props.hazMgtStorageLocation.refHazLicenseTo.id }),
                        optLabel: "name",
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refStorageLocation = value;
                            record.refStorageLocationSub = null;
                            record.amount = null;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            width: 200
        })
    );
    columns.push(
        createColumnInlineSelect({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refStorageLocationSub"),
            dataIndex: "refStorageLocationSub",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || !record.refStorageLocation,
                        list: ({ searchQuery }) =>
                            callStroageLocationSub({
                                searchQuery,
                                refStorageLocationId: record.refStorageLocation?.id,
                                notIds: items
                                    .filter(d => d.isDelete !== true)
                                    .map(d => d.refStorageLocationSub?.id)
                                    .filter(d => d)
                            }),
                        optLabel: "buildingName",
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refStorageLocationSub = value;
                            record.amount = null;
                            if (value) {
                                setIsLoadingApp(true);
                                callGetStorageLocationSubReserveAndUse({ stroageLocationSubId: value.id })
                                    .then(d => {
                                        record.refStorageLocationSub.useKgReserve = d.reserve;
                                        record.refStorageLocationSub.useKgUse = d.use;
                                    })
                                    .finally(() => setIsLoadingApp(false));
                            }
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            width: 200
        })
    );
    columns.push(
        createColumnInlineInputNumber({
            title: () => i18n.global.t("views.sellBuy.storageLocationStore"),
            dataIndex: "amount",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ allowPrecision: true }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || !record.refStorageLocationSub,
                        allowPrecision: true,
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.amount = value;
                        },
                        onBlur: () => useForm.validate(items.map((d, index) => `${itemsKey}.${index}.amount`)).catch(() => {})
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                )
        })
    );
    columns.push(
        createColumnInputNumber({
            title: () => i18n.global.t("views.sellBuy.storageLocationBalance"),
            align: "center",
            customRender: param => {
                const itemOld = itemOlds.find(d => d.refStorageLocationSub?.id === param.record.refStorageLocationSub?.id);
                console.log("param.record.refStorageLocationSub?.useKg", param.record.refStorageLocationSub?.useKg)
                console.log("param.record.refStorageLocationSub?.useKgReserve", param.record.refStorageLocationSub?.useKgReserve)
                console.log("param.record.refStorageLocationSub?.useKgUse", param.record.refStorageLocationSub?.useKgUse)
                console.log("itemOld?.amount", itemOld?.amount)
                console.log("param.record.amount", param.record.amount)
                return (
                    <Number
                        {...param.column}
                        {...param}
                        text={
                            (param.record.refStorageLocationSub?.useKg -
                                param.record.refStorageLocationSub?.useKgReserve -
                                param.record.refStorageLocationSub?.useKgUse || 0) +
                            parseFloat(itemOld?.amount || 0) -
                            parseFloat(param.record.amount || 0)
                        }
                    ></Number>
                );
            }
        })
    );
    return columns;
};

export const initData = ({ props, data }) => {
    const { menuContext, viewOnly, itemsKey, canCreate, canDelete } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    data.columns = createColumns({ props, data });
    data.page = createPage({ data: items });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => (viewOnly ? false : canDelete),
            onClick: ({ rowSelection }) => {
                onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey });
                data.page.page = 0;
            }
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [
        {
            render: () => createButtonCreate({ onClick: () => items.push({}) }),
            visible: () => (viewOnly ? false : canCreate)
        }
    ];
    data.rowActions = [];
    fetchData({ props, data }).then(page => items.push(...page.data));
};

export const fetchData = ({ props }) => {
    const { fetchCriteria } = props;
    if (!fetchCriteria || fetchCriteria.fetched) {
        return Promise.resolve({ data: [] });
    }
    fetchCriteria.fetched = true;
    setIsLoading(true);
    return callSearch({ criteria: fetchCriteria }).finally(() => setIsLoading(false));
};
